import * as React from "react";
import PropTypes from "prop-types";
import ThermostatSetpointControls from "./ThermostatSetpointControls";
import PivotThermostatSetpointControls from "./PivotThermostatSetpointControls";
import { updateHeatSetpoint, updateCoolSetpoint } from "./calculateSetpoint";
import ScreenReaderOnlyLiveRegion from "../ScreenReaderOnlyLiveRegion";
import UserLanguageContext from "../ContextWrappers/UserLanguageContext";

export default class ThermostatSetpoints extends React.Component {
  static propTypes = {
    onChangesStarted: PropTypes.func,
    onChangesEnded: PropTypes.func,
    onChangesCanceled: PropTypes.func,
    changeBufferMs: PropTypes.number,
    formatter: PropTypes.func,
    setpointHeat: PropTypes.number,
    setpointHeatMin: PropTypes.number,
    setpointHeatMax: PropTypes.number,
    setpointCool: PropTypes.number,
    setpointCoolMin: PropTypes.number,
    setpointCoolMax: PropTypes.number,
    setpointIncrement: PropTypes.number,
    // the delta is used internally by calculateSetpoint
    setpointDelta: PropTypes.number, // eslint-disable-line react/no-unused-prop-types
    occuLabel: PropTypes.string,
    occuLabelStyle: PropTypes.object,
    isCommercial: PropTypes.bool,
  };

  static defaultProps = {
    changeBufferMs: 2000,
    setpointDelta: 3,
  };

  state = {
    setpointHeat: this.props.setpointHeat,
    setpointCool: this.props.setpointCool,
    changesInProgressCount: 0,
  };

  UNSAFE_componentWillMount() {
    this._isMounted = true;
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setState({
      setpointHeat: nextProps.setpointHeat,
      setpointCool: nextProps.setpointCool,
    });
  }

  componentWillUnmount() {
    if (this.state.changesInProgressCount > 0 && this.props.onChangesCanceled) {
      this.props.onChangesCanceled({
        setpointHeat: this.state.setpointHeat,
        setpointCool: this.state.setpointCool,
      });
    }
    this.setState({ changesInProgressCount: 0 });

    this._isMounted = false;
  }

  handleHeatValue = (heatSetpoint) => {
    this._mostRecentlyChangedValue = "heat";
    this.setState((prevState) => {
      const info = { ...this.props, ...prevState };
      return updateHeatSetpoint(info, heatSetpoint);
    });
  };

  handleCoolValue = (coolSetpoint) => {
    this._mostRecentlyChangedValue = "cool";
    this.setState((prevState) => {
      const info = { ...this.props, ...prevState };
      return updateCoolSetpoint(info, coolSetpoint);
    });
  };

  handleChangesStarted = () => {
    if (!this._isMounted) {
      return;
    }

    this._initialSetpoints = {
      setpointHeat: this.state.setpointHeat,
      setpointCool: this.state.setpointCool,
    };

    // If there haven't been any changes yet, call onChangesStarted
    if (
      this.state.changesInProgressCount === 0 &&
      this.props.onChangesStarted
    ) {
      this.props.onChangesStarted(this._initialSetpoints);
    }
    this.setState((prevState) => ({
      changesInProgressCount: prevState.changesInProgressCount + 1,
    }));
  };

  handleChangesEnded = (aborted) => {
    if (!this._isMounted) {
      return;
    }
    const { changesInProgressCount, setpointHeat, setpointCool } = this.state;

    let newStateUpdater;
    let finalSetpoints;

    if (aborted) {
      newStateUpdater = (prevState) => ({
        changesInProgressCount: prevState.changesInProgressCount - 1,
        ...this._initialSetpoints,
      });
      finalSetpoints = this._initialSetpoints;
    } else {
      newStateUpdater = (prevState) => ({
        changesInProgressCount: prevState.changesInProgressCount - 1,
      });
      finalSetpoints = { setpointHeat, setpointCool };
    }

    this.setState(newStateUpdater);
    // If this is the only change and it's about to end, call onChangesEnded
    if (changesInProgressCount === 1 && this.props.onChangesEnded) {
      this.props.onChangesEnded(finalSetpoints);
    }
  };

  render() {
    const {
      setpointIncrement,
      setpointCoolMin,
      setpointCoolMax,
      formatter,
      changeBufferMs,
      setpointHeatMin,
      setpointHeatMax,
      occuLabel,
      occuLabelStyle,
      isCommercial,
    } = this.props;

    const { setpointHeat, setpointCool } = this.state;
    const languageContext = this.context;
    const language =
      languageContext.localeSet.nx_components.ThermostatSetpoints;

    let valueWidth = 50;

    if (setpointIncrement < 1) {
      if (isCommercial) {
        valueWidth = 75;
      } else {
        valueWidth = 70;
      }
    }

    const styles = {
      value: {
        width: valueWidth,
      },
    };

    const valuesRegionId = "nx-components_ThermostatSetpoints_values";
    let valuesRegionContent = null;
    if (this.props.setpointCool != null && this.props.setpointHeat != null) {
      if (this._mostRecentlyChangedValue === "heat") {
        // If heat was recently changed, then announce the heat value first
        // instead of the cool value, since that's more relevant.
        valuesRegionContent = language.heatToCoolTo;
      } else {
        valuesRegionContent = language.coolToHeatTo;
      }
    } else if (this.props.setpointCool != null) {
      valuesRegionContent = language.coolTo;
    } else if (this.props.setpointHeat != null) {
      valuesRegionContent = language.heatTo;
    }

    return (
      <div data-component-name="ThermostatSetpoints">
        {valuesRegionContent && (
          <ScreenReaderOnlyLiveRegion id={valuesRegionId}>
            {valuesRegionContent}
          </ScreenReaderOnlyLiveRegion>
        )}
        {isCommercial ? (
          <PivotThermostatSetpointControls
            setpointIncrement={setpointIncrement}
            setpointCoolMin={setpointCoolMin}
            setpointCoolMax={setpointCoolMax}
            setpointCool={setpointCool}
            setpointHeatMin={setpointHeatMin}
            setpointHeatMax={setpointHeatMax}
            setpointHeat={setpointHeat}
            onCoolValue={this.handleCoolValue}
            onHeatValue={this.handleHeatValue}
            onChangesStarted={this.handleChangesStarted}
            onChangesEnd={this.handleChangesEnded}
            changeBufferMs={changeBufferMs}
            valueStyle={styles.value}
            hasAriaControls={Boolean(valuesRegionContent)}
            occuLabel={occuLabel}
            occuLabelStyle={occuLabelStyle}
          />
        ) : (
          <ThermostatSetpointControls
            setpointIncrement={setpointIncrement}
            setpointCoolMin={setpointCoolMin}
            setpointCoolMax={setpointCoolMax}
            setpointCool={setpointCool}
            setpointHeatMin={setpointHeatMin}
            setpointHeatMax={setpointHeatMax}
            setpointHeat={setpointHeat}
            onCoolValue={this.handleCoolValue}
            onHeatValue={this.handleHeatValue}
            onChangesStarted={this.handleChangesStarted}
            onChangesEnd={this.handleChangesEnded}
            formatter={formatter}
            changeBufferMs={changeBufferMs}
            valueStyle={styles.value}
            hasAriaControls={Boolean(valuesRegionContent)}
          />
        )}
      </div>
    );
  }
}

ThermostatSetpoints.contextType = UserLanguageContext;



// WEBPACK FOOTER //
// ../nx-components/src/Thermostat/ThermostatSetpoints.jsx